/**
 * When the "Ask MAHI" lockup should play its ripple.
 *
 * The point of the beat is to remind someone that the conversation exists at
 * the moment it would actually help them, so this is deliberately *not* a
 * wall-clock interval. A timer fires mostly while the shopper is mid-swipe and
 * not looking at the bottom row; these three moments are the ones where the
 * reminder lands:
 *
 *   1. Once shortly after the deck becomes interactive, so the control is
 *      noticed at all on a first visit.
 *   2. On idle — no swipe, no tap, for IDLE_MS. Hesitation is precisely when
 *      "just ask me" is the answer, so this is the highest-value trigger.
 *   3. When the deck runs thin. Conversation is the escape hatch that stops a
 *      thinning feed from becoming a dead end, so the nudge has to arrive
 *      *before* the shopper runs out of cards rather than after.
 *
 * Two guards keep it from becoming nagging: FLOOR_MS between any two beats,
 * and MAX_BEATS in total for the life of the component. A dropped beat is just
 * dropped — nothing queues, because a nudge that arrives late is a nudge that
 * arrives at the wrong moment.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const INTRO_MS = 4000;   // after mount, so the control is seen once
const IDLE_MS = 15000;   // no activity — the shopper is hesitating
const FLOOR_MS = 45000;  // minimum gap between beats
const MAX_BEATS = 3;     // per mount, then it goes quiet for good

/**
 * `activityKey` is any value that changes on every swipe/tap — the idle timer
 * restarts whenever it does. `deckThin` fires a beat on its rising edge only.
 * Returns a counter; each increment means "play one ripple now".
 */
export default function useAskBeat({ activityKey, deckThin = false, enabled = true } = {}) {
  const [beat, setBeat] = useState(0);
  const lastAtRef = useRef(0);
  const countRef = useRef(0);

  const fire = useCallback(() => {
    if (!enabled) return;
    if (countRef.current >= MAX_BEATS) return;
    const now = Date.now();
    if (now - lastAtRef.current < FLOOR_MS) return;
    lastAtRef.current = now;
    countRef.current += 1;
    setBeat((b) => b + 1);
  }, [enabled]);

  // (1) the introduction beat.
  useEffect(() => {
    const t = setTimeout(fire, INTRO_MS);
    return () => clearTimeout(t);
  }, [fire]);

  // (2) idle — restarted by every change of activityKey.
  useEffect(() => {
    const t = setTimeout(fire, IDLE_MS);
    return () => clearTimeout(t);
  }, [activityKey, fire]);

  // (3) the deck thinning out, on the rising edge only.
  const wasThinRef = useRef(deckThin);
  useEffect(() => {
    if (deckThin && !wasThinRef.current) fire();
    wasThinRef.current = deckThin;
  }, [deckThin, fire]);

  return beat;
}
